import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import AutocompleteInput from "../hris/AutocompleteInput";

/**
 * Pemilih pelanggan B2B/USER dari kartu pelanggan (brief owner 22 Sep 2026, B2).
 *
 * Owner: *"pilih nama pelanggan untuk B2B dan USER menggunakan dropdown —
 * supaya penamaan pelanggan bertempo konsisten, bisa ditagih sekaligus tanpa
 * takut typo"*. Surat "Tagih Sekaligus" mengelompokkan piutang lewat TEKS
 * nama (`piutang.py::_kunci_nama_pelanggan`), jadi satu typo memecah tagihan
 * satu pelanggan jadi dua surat. G2 (15 Sep) cuma memberi SARAN; owner
 * mengulang butirnya, jadi sekarang WAJIB dipilih.
 *
 * SATU komponen untuk Input Nota DAN Edit Penjualan — dua salinan dari hal
 * yang sama sudah berkali-kali berpisah jalan di repo ini (CLAUDE.md:
 * "Duplikasi helper melahirkan penyimpangan").
 *
 * - Memilih kartu mengisi nama DAN No HP sekaligus (`onPilih`). Nama dari
 *   kartu A dengan nomor B melahirkan pasangan yang ditolak server dan
 *   memecah kredit retur/DP pelanggan yang sama.
 * - Mengetik ulang nama melepas pilihannya — pemanggil membandingkan lewat
 *   `kartuCocok()` di bawah, bukan sekadar "pernah memilih".
 * - "+ Pelanggan baru" mendaftarkan kartu (nama + No HP wajib) lewat
 *   `POST /pelanggan/untuk-nota`. Tanpa pintu ini penjualan ke pelanggan B2B
 *   yang baru pertama datang mati total. Tombolnya digerbangi kunci yang SAMA
 *   dengan server (`pelanggan.py::BOLEH_DAFTAR_PELANGGAN_NOTA`, lihat
 *   `KUNCI_DAFTAR_PELANGGAN_NOTA` di bawah).
 *
 * Server menegakkan aturan yang sama di SEMUA jalur tulis
 * (`pelanggan.kanonikal_pelanggan_nota`); komponen ini hanya supaya kasir
 * tidak menabrak 400 tanpa tahu jalan keluarnya.
 */
export function kartuCocok(kartu, nama, noHp) {
  return !!kartu && kartu.nama === nama && kartu.no_hp === noHp;
}

/** Kunci izin yang boleh MENDAFTARKAN kartu lewat "+ Pelanggan baru" — cermin
 *  PERSIS `pelanggan.py::BOLEH_DAFTAR_PELANGGAN_NOTA` (diuji backend). Izin
 *  menulis nota, BUKAN `pelanggan.view`: akun baca-saja dulu melihat tombol ini
 *  dan server meloloskannya, padahal kartu yang ia buat mengunci ejaan nama
 *  seluruh nota B2B/USER untuk nomor itu. */
export const KUNCI_DAFTAR_PELANGGAN_NOTA = [
  ["nota_sales", "create"],
  ["nota_sales", "update"],
  ["sales", "create"],
  ["sales", "update"],
  ["pelanggan", "create"],
];

/** Cari kartu pelanggan di SERVER — menyaring daftar yang terpotong di layar
 *  membuat pelanggan ke-101 tidak pernah ketemu (lihat docstring
 *  `cari_pelanggan_untuk_nota`). Gagal = tanpa saran, bukan tanpa form. */
async function cariPelanggan(q) {
  try {
    const { data } = await api.get("/pelanggan/cari-untuk-nota", { params: { q, limit: 10 } });
    return (data.items || []).map(p => ({ ...p, value: p.nama }));
  } catch {
    return [];
  }
}

function barisPelanggan(p) {
  return (
    <div className="min-w-0 flex-1">
      <p className="text-slate-900 truncate font-medium">{p.nama}</p>
      <p className="text-[10px] text-slate-500 font-mono">
        {p.no_hp || "tanpa no HP"}
        {p.terakhir_datang ? ` · terakhir ${p.terakhir_datang}` : ""}
      </p>
    </div>
  );
}

export default function PilihPelangganKartu({
  nama, noHp, kartu, onUbahNama, onPilih,
  className = "w-full h-9 px-3 rounded-lg border border-slate-200 text-sm",
  "data-testid": testid = "input-pelanggan",
}) {
  const { can } = useAuth();
  const bolehDaftar = KUNCI_DAFTAR_PELANGGAN_NOTA.some(([m, a]) => can(m, a));
  const [baru, setBaru] = useState(null);   // {nama, no_hp, busy, galat}
  const cocok = kartuCocok(kartu, nama, noHp);

  const simpanBaru = async () => {
    if (!(baru.nama || "").trim() || !(baru.no_hp || "").trim()) {
      setBaru(x => ({ ...x, galat: "Nama dan No HP wajib diisi" }));
      return;
    }
    setBaru(x => ({ ...x, busy: true, galat: "" }));
    try {
      const { data } = await api.post("/pelanggan/untuk-nota",
        { nama: baru.nama.trim(), no_hp: baru.no_hp.trim() });
      const k = data.item || {};
      onPilih({ nama: k.nama, no_hp: k.no_hp });
      toast.success(data.message || "Pelanggan terdaftar");
      setBaru(null);
    } catch (e) {
      setBaru(x => ({ ...x, busy: false,
        galat: e?.response?.data?.detail || "Gagal mendaftarkan pelanggan" }));
    }
  };

  return (
    <>
      <AutocompleteInput
        value={nama}
        onChange={onUbahNama}
        onPick={p => onPilih({ nama: p.nama, no_hp: p.no_hp || "" })}
        fetcher={cariPelanggan}
        minChars={2}
        renderRow={barisPelanggan}
        placeholder="Ketik 2 huruf untuk mencari pelanggan…"
        data-testid={testid}
        className={className}
      />
      <div className="flex items-center justify-between gap-2 mt-1">
        {cocok ? (
          <span className="text-[11px] text-emerald-700" data-testid={`${testid}-terpilih`}>
            Dari kartu pelanggan
          </span>
        ) : (
          <span className="text-[11px] text-amber-700" data-testid={`${testid}-belum-dipilih`}>
            Wajib dipilih dari daftar
          </span>
        )}
        {bolehDaftar && (
          <button type="button" data-testid={`${testid}-baru`}
            onClick={() => setBaru({ nama: cocok ? "" : nama, no_hp: cocok ? "" : noHp,
                                     busy: false, galat: "" })}
            className="text-[11px] font-medium text-sky-700 hover:underline">
            + Pelanggan baru
          </button>
        )}
      </div>

      {/* Lewat PORTAL ke <body>: pemanggil membungkus kolom ini dengan
          <label>, dan klik di dalam dialog yang masih jadi keturunan <label>
          ikut "mengaktifkan" kontrol pertamanya (kotak cari di belakang).
          z-[60]: di atas modal form (z-50) yang masih terbuka. */}
      {baru && createPortal(
        // stopPropagation: event React dari portal tetap merambat ke leluhur
        // KOMPONEN-nya (modal form) — tanpa ini klik latar dialog ini bisa
        // ikut dibaca sebagai klik latar modal form dan menutupnya.
        <div className="fixed inset-0 z-[60] bg-black/60 grid place-items-center p-4"
          onClick={e => { e.stopPropagation(); setBaru(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl text-left" onClick={e => e.stopPropagation()}
            data-testid={`${testid}-baru-modal`}>
            <div className="bg-sky-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm">Daftarkan Pelanggan Baru</h3>
              <button type="button" onClick={() => setBaru(null)} className="p-1.5 hover:bg-white/20 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <label className="block">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  Nama Pelanggan *
                </span>
                <input value={baru.nama} autoFocus data-testid={`${testid}-baru-nama`}
                  onChange={e => setBaru(x => ({ ...x, nama: e.target.value, galat: "" }))}
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
              </label>
              <label className="block">
                <span className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-1">
                  No HP * (minimal 10 digit)
                </span>
                <input value={baru.no_hp} inputMode="tel" data-testid={`${testid}-baru-hp`}
                  onChange={e => setBaru(x => ({ ...x, no_hp: e.target.value, galat: "" }))}
                  placeholder="08xxxxxxxxxx"
                  className="w-full h-9 px-3 rounded-lg border border-slate-200 text-sm" />
              </label>
              {baru.galat && (
                <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5"
                  data-testid={`${testid}-baru-galat`}>{baru.galat}</p>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={() => setBaru(null)}
                  className="h-9 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button type="button" onClick={simpanBaru} disabled={baru.busy}
                  data-testid={`${testid}-baru-simpan`}
                  className="h-9 px-4 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-medium disabled:opacity-50">
                  {baru.busy ? "Menyimpan..." : "Daftarkan & Pakai"}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
